<template>
  <div class="hint-button__wrapper">
    <img
      src="@/assets/images/hints/hint-button.gif"
      class="hint-icon--gif __desktop"
      @click="handleGifButtonClick()"
      v-if="!isGifButtonClicked"
      @mouseenter="handleHoverOnButton('in')"
      @mouseleave="handleHoverOnButton('out')"
      v-tooltip="{
        content: tooltipContent,
        offset: 0,
      }"
    />
    <img
      src="@/assets/images/hints/hint-button.gif"
      class="hint-icon--gif __mobile"
      @click="handleGifButtonClick()"
      v-if="!isGifButtonClicked"
      v-tooltip="$i18nPresenter.t('Add hints to aid players')"
      @mouseenter="handleHoverOnButton('in')"
      @mouseleave="handleHoverOnButton('out')"
    />
    <div
      v-show="isHintButtonVisible"
      class="hint-icon__wrapper"
      :class="{'active': slideActive.isHintsVisible}"
      @click="toggleHintsSection()"
      @mouseenter="handleHoverOnButton('in')"
      @mouseleave="handleHoverOnButton('out')"
    >
      <aha-icon
        name="hint"
        width="25"
        height="25"
        viewBox="0 0 17 17"
        class="hint-icon __desktop"
        v-tooltip="{
          content: tooltipContent,
          offset: 0,
        }"
      />
      <aha-icon
        name="hint"
        width="25"
        height="25"
        viewBox="0 0 17 17"
        class="hint-icon __mobile"
        v-tooltip="$i18nPresenter.t('Add hints to aid players')"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import hintTooltipVideo from '@/assets/images/hints/hint-onboarding.webm'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import SlideApi from '@/api/slide'
import Socket from '@/socket'

const MAX_HOVER_TIME = 4

export default {
  data() {
    return {
      hintTooltipVideo,
      tooltipContent: 'Add hints to aid players',
      isGifButtonClicked: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
    }),
    isPickAnswerOrPickImage() {
      const { id, type, slideType } = this.slideActive
      return id && type === 'pickAnswer' && (!slideType || slideType === 'imageChoice')
    },
    isHintButtonVisible() {
      return this.isPickAnswerOrPickImage && !this.presentation.audiencePacing
      && this.isGifButtonClicked
    },
    hintKey() {
      return `hintToolTipVisible-${this.currentUser.id}`
    },
    gifButtonKey() {
      return `hintGifButtonVisible-${this.currentUser.id}`
    },
  },
  mounted() {
    this.isGifButtonClicked = localStorageExpiry.getWithExpiry(this.gifButtonKey)
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    handleGifButtonClick() {
      const isClicked = localStorageExpiry.getWithExpiry(this.gifButtonKey)
      if (isClicked) return
      this.toggleHintsSection()
      if (!isClicked) {
        localStorageExpiry.setWithExpiry(this.gifButtonKey, true, 60 * 24 * 60 * 60 * 1000)
        this.isGifButtonClicked = true
      }
    },
    toggleHintsSection() {
      try {
        const payload = {
          isHintsVisible: !this.slideActive.isHintsVisible,
          socketId: Socket.getSocketId(), // for real-time collab
          uniqueAccessCode: this.presentation.uniqueAccessCode,
        }
        this.updateSlideActiveAction(payload)
        SlideApi.patchSlideById(this.slideActive.id, payload)
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Quiz hint > Show/hide button',
          'Quiz hint',
          '',
          {
            slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
              this.slideActive.slideType),
            slideId: this.slideActive.id,
            presentationId: this.presentation.id,
          },
        )
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    handleHoverOnButton(mode) {
      if (mode === 'in') {
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Quiz hint > Hover',
          'Quiz hint',
          '',
          {
            slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
              this.slideActive.slideType),
            slideId: this.slideActive.id,
            presentationId: this.presentation.id,
          },
        )
        let hintTimes = localStorageExpiry.getWithExpiry(this.hintKey)
        if (!hintTimes) {
          // expires in 2 months
          localStorageExpiry.setWithExpiry(this.hintKey, 1, 60 * 24 * 60 * 60 * 1000)
          this.tooltipContent = `
            <div class="pl-0.5 pb-1">Add hints to aid players</div>
            <div class="w-52 pb-2">
              <video width="224" height="142" id="hint-video" autoplay loop>
                <source src='${this.hintTooltipVideo}' type="video/webm">
                Your browser does not support the video tag.
              </video>
            </div>
          `
          this._setPlayVideo(mode)
          return
        }
        if (hintTimes < MAX_HOVER_TIME) {
          hintTimes++
          localStorageExpiry.setWithExpiry(this.hintKey, hintTimes,
            60 * 24 * 60 * 60 * 1000)
          return
        }
        if (hintTimes >= MAX_HOVER_TIME) {
          this.tooltipContent = this.$i18nPresenter.t('Add hints to aid players')
        }
      }
    },
    _setPlayVideo(mode) {
      const vid = document.getElementById('hint-video')
      if (!vid) return
      if (mode === 'in') {
        vid.currentTime = 0
        vid.play().catch(() => {})
      } else {
        vid.pause()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hint-button__wrapper {
  display: inline;
}
.hint-icon--gif {
  cursor: pointer;
  width: 30px;
  height: 30px;
  margin-top: -6px;
  margin-left: 1px;
  &.__desktop {
    display: inline;
  }
  &.__mobile {
    display: none;
  }
}

.hint-icon__wrapper {
  display: inline-block;
  cursor: pointer;
  padding: 3px 2px 1px 2px;
  border-radius: 5px;
  margin-left: 3px;
  @apply border border-transparent;
  &:hover {
    @apply bg-secondary-15;
    .hint-icon {
      color: #282E3E;
    }
  }
  &.active {
    @apply bg-secondary-15 border border-base-50;
  }
  .hint-icon {
    color: #525F7F;
    margin-top: -6px;
    &.__desktop {
      display: inline;
    }
    &.__mobile {
      display: none;
    }
  }
}
@media (max-width: 1180px) {
  .hint-button__wrapper {
    .hint-icon, .hint-icon--gif {
      &.__desktop {
        display: none;
      }
      &.__mobile {
        display: inline;
      }
    }
  }
}
</style>
